import React, { useState, useRef, useEffect } from 'react';
import { AIProvider, Conversation, Message } from '../types/ai';
import { User, Tenant } from '../types/user';
import { ChatMessage } from './ChatMessage';
import { MessageInput } from './MessageInput';
import { ChatDropZone } from './ChatDropZone';

interface MobileChatLayoutProps {
  user: User;
  tenant: Tenant;
  provider: AIProvider;
  modelId: string;
  conversation: Conversation | null;
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (content: string) => void;
  onBackToHome: () => void;
  onMenuClick: () => void;
}

export function MobileChatLayout({
  user,
  tenant,
  provider,
  modelId,
  conversation,
  messages,
  isLoading,
  onSendMessage,
  onBackToHome,
  onMenuClick
}: MobileChatLayoutProps) {
  const [inputValue, setInputValue] = useState('');
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll to bottom quando mensagens mudam
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Scroll para o final ao abrir uma conversa
  useEffect(() => {
    if (conversation) {
      // Usar setTimeout para garantir que o DOM foi renderizado
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [conversation?.id]);

  const handleSend = (files?: File[]) => {
    if ((inputValue.trim() || files) && !isLoading) {
      console.log('📎 Arquivos para enviar:', files);
      
      const messageToSend = inputValue.trim();
      // Limpa o input imediatamente para permitir digitar a próxima mensagem
      setInputValue('');
      setDroppedFiles([]);
      
      onSendMessage(messageToSend);
    }
  };

  const handleFilesDropped = (files: File[]) => {
    console.log('🎯 MobileChatLayout - Arquivos dropados:', files.map(f => f.name));
    setDroppedFiles(files);
  };

  const currentModel = provider.models.find(m => m.id === modelId);

  return (
    <ChatDropZone onFilesDropped={handleFilesDropped} disabled={isLoading}>
      <div className="fixed inset-0 flex flex-col bg-gray-50">
      {/* Fixed Top Bar */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-sm border-b px-4 py-3 flex items-center justify-between z-10">
        <div className="flex items-center space-x-3">
          <button
            onClick={onMenuClick}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
              {provider.id === 'openai' && (
                <img src="/gpt-logo.png" alt="GPT" className="w-5 h-5 object-contain" />
              )}
              {provider.id === 'anthropic' && (
                <img src="/claude-logo.png" alt="Claude" className="w-5 h-5 object-contain" />
              )}
              {provider.id === 'google' && (
                <img src="/gemini-logo.png" alt="Gemini" className="w-5 h-5 object-contain" />
              )}
              {!['openai', 'anthropic', 'google'].includes(provider.id) && (
                <span className="text-gray-600 text-sm">🤖</span>
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-gray-900 truncate">
                {currentModel?.name || provider.name}
              </h1>
            </div>
          </div>
        </div>

        <button
          onClick={onBackToHome}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Scrollable Messages Area */}
      <div className="flex-1 overflow-y-auto px-4 pt-[60px] pb-[80px]">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-4">
              {provider.id === 'openai' && (
                <img src="/gpt-logo.png" alt="GPT" className="w-8 h-8 object-contain" />
              )}
              {provider.id === 'anthropic' && (
                <img src="/claude-logo.png" alt="Claude" className="w-8 h-8 object-contain" />
              )}
              {provider.id === 'google' && (
                <img src="/gemini-logo.png" alt="Gemini" className="w-8 h-8 object-contain" />
              )}
              {provider.id === 'perplexity' && (
                <img src="/perplexity-logo.png" alt="Perplexity" className="w-8 h-8 object-contain" />
              )}
              {!['openai', 'anthropic', 'google', 'perplexity'].includes(provider.id) && (
                <span className="text-white text-xl">🤖</span>
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {currentModel?.name || provider.name}
            </h3>
            <p className="text-gray-600 text-sm max-w-sm">
              {currentModel?.description || provider.description}
            </p>
            <p className="text-gray-500 text-xs mt-2">
              Digite uma mensagem para começar a conversar
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => (
              <ChatMessage
                key={index}
                message={message}
                provider={provider}
                isLast={index === messages.length - 1}
                generatedFiles={message.generatedFiles}
              />
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-lg p-3 shadow-sm border max-w-xs">
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                    <span className="text-sm text-gray-600">Digitando...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Fixed Input Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-3 z-10">
        <MessageInput
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSend}
          placeholder="Insira seu comando para a SPACE"
          disabled={isLoading}
          externalFiles={droppedFiles}
          userId={user.id}
        />
      </div>
    </div>
    </ChatDropZone>
  );
}
